<template>
  <div
    class="radio"
    :class="[variant, flow, {hasError, hasSuccess, isDisabled: (attrs.disabled !== undefined || attrs.disabled === true)}]"
  >
    <div class="inputgroup-wrapper">
      <div
        v-for="(rb, optionIndex) in options"
        :key="`${inputId}-${optionIndex}`"
        class="input-wrapper"
        :class="[{isChecked: computedModelValue===rb.value, isDisabled: rb.disabled}]"
      >
        <input
          :id="`${inputId}-${optionIndex}`"
          v-model="computedModelValue"
          type="radio"
          :value="rb.value"
          :name="attrs.name"
          :required="attrs.required"
          :disabled="rb.disabled || attrs.disabled"
          @blur="emit('blur', $event)"
        />
        <label :for="`${inputId}-${optionIndex}`" v-html="rb.label"></label>
      </div>
    </div>

    <div v-if="errorMessage && hasError" class="text-danger text-s message-wrapper message-error">
      <span>{{ errorMessage }}</span>
      <Icon name="warning-fill" color="danger" />
    </div>
  </div>
</template>

<script setup>
import { inject, useAttrs, ref, computed, useId } from 'vue'
import Icon from '../Icon'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },
  options: {
    type: [Array, Object],
    required: true,
  },
  flow: {
    type: String,
    default: 'row',
  },
  errorMessage: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  hasSuccess: {
    type: Boolean,
    default: false,
  },
  trackingLabel: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'normal',
    validator: (value) => ['normal', 'advanced', 'normal-tick', 'advanced-tick', ''].includes(value),
  },
})

const localModelValue = ref()

const attrs = useAttrs()
const inputId = attrs.id ? attrs.id : useId()

const emit = defineEmits(['update:modelValue', 'blur'])

const Track = inject('track', props.trackingLabel)
const track = () => {
  if (props.trackingLabel) {
    Track('form-radio-select', props.trackingLabel)
  }
}

const computedModelValue = computed({
  get() {
    return typeof props.modelValue !== 'object' ? props.modelValue : localModelValue.value
  },
  set(newValue) {
    track()
    emit('update:modelValue', newValue)
    localModelValue.value = newValue
  },
})
</script>

<style scoped lang="scss">
.radio {
  display: flex;
  flex-direction: column;
  gap: .25rem;

  --radioSize: 1.333rem;
  --radioBorderColor: var(--mint-dark);
  --tickHeight: .55rem;
  --tickWidth: .25rem;
  --tickThinkness: .15rem;
  --tickColor: var(--white);
}

.radio.column {
  width: 100%;
}

.radio .inputgroup-wrapper {
  display: flex;
  flex-flow: row wrap;
  gap: 1rem 1.5rem;
}

.radio.column .inputgroup-wrapper {
  flex-direction: column;
}

.radio .input-wrapper {
  display: flex;
  gap: 1rem;
  transition-duration: var(--time1);
  transition-timing-function: ease-in-out;
  transition-property: background-color, border-color;

  /** We need to disable the transition on webkit browsers because it causes
  a restart of the animation for the checkbox on every hover-change. Only 
  happens in Safari > v.17. */
  @supports (hanging-punctuation: first) and (font: -apple-system-body) and (-webkit-appearance: none) {
    transition: none;
  } 
}

.radio .input-wrapper input {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  appearance: none;
  height: var(--radioSize);
  width: var(--radioSize);
  margin: 0;
  background-color: var(--white);
  border: 1px solid var(--radioBorderColor);
  border-radius: 50%;
  font: inherit;
  color: var(--white);
  cursor: pointer;
  position: relative;
}

.radio .input-wrapper input:not(:disabled):is(:active, :focus) {
  box-shadow: 0 0 0 1px var(--mint-dark);
}

.radio .input-wrapper input:disabled {
  cursor: not-allowed;

  --tickColor: var(--neutral-20);
  --radioBorderColor: var(--neutral-20);
  background-color: var(--neutral-10);
}

.radio .input-wrapper input:not(:disabled):checked::before {
  content: "";
  width: var(--radioSize);
  height: var(--radioSize);
  flex-shrink: 0;
  background-color: var(--success);
  border-radius: 50%;
  transform: scale(.6);
  animation: radio-bubble var(--time2) ease-in-out forwards;
}

@keyframes radio-bubble{
  0%{
    transform: scale(0);
  }

  66%{
    transform: scale(.7);
  }

  100%{
    transform: scale(.6);
  }
}

.radio:is(.normal-tick, .advanced-tick) .input-wrapper input:checked:not(:disabled) {
  border-color: var(--mint-lighter);
  background-color: var(--success);
}

.radio:is(.normal-tick, .advanced-tick) .input-wrapper input:checked::before {
  box-sizing: initial;
  content: "";
  position: absolute;
  top: calc(var(--radioSize) * 0.5 - var(--tickThinkness));
  left: calc(var(--radioSize) * 0.5 - var(--tickHeight) + var(--tickThinkness));
  width: 0;
  height: 0;
  border-right: var(--tickThinkness) solid var(--transparent);
  border-bottom: var(--tickThinkness) solid var(--transparent);
  border-radius: 0;
  transform: rotate(45deg);
  transform-origin: 0% 100%;
  animation: checkbox-check var(--time2) ease-in-out forwards;
}

@keyframes checkbox-check{
  0%{
    width: 0;
    height: 0;
    border-color: var(--tickColor);
    transform: translate3d(0, 0, 0) rotate(45deg);
  }

  33%{
    width: var(--tickWidth);
    height: 0;
    transform: translate3d(0, 0, 0) rotate(45deg);
  }

  100%{
    width: var(--tickWidth);
    height: var(--tickHeight);
    border-color: var(--tickColor);
    transform: translate3d(0,calc(#{var(--tickHeight)} * -1), 0) rotate(45deg);
  }
}

.radio:is(.advanced, .advanced-tick) .input-wrapper {
  padding: 1rem;
  border-radius: .25rem;
  border: 1px solid var(--mint-dark);
  background-color: var(--mint-lightest);
}

.radio:is(.advanced, .advanced-tick) .input-wrapper:hover,
.radio:is(.advanced, .advanced-tick) .input-wrapper:focus-within {
  border-color: var(--mint-darker);
  background-color: var(--mint-light);
}

.radio:is(.advanced, .advanced-tick) .input-wrapper.isChecked {
  border-color: var(--mint);
  background-color: var(--mint);
}

.radio:is(.advanced, .advanced-tick) .input-wrapper.isChecked:hover,
.radio:is(.advanced, .advanced-tick) .input-wrapper.isChecked:focus-within {
  border-color: var(--mint-darker);
}

.radio:is(.advanced, .advanced-tick).hasError .input-wrapper {
  border-color: var(--danger);
}

.radio:is(.advanced, .advanced-tick).hasSuccess .input-wrapper {
  border-color: var(--success);
}

.radio .input-wrapper label {
  width: 100%;
  transform: translateY(-.1rem);
}

.radio:not(.column) .input-wrapper label:empty {
  display: none;
}

.radio .message-wrapper {
  display: grid;
  grid-template-columns: 1fr 1rem;
  gap: .25rem;
  padding-right: .75rem;
}
</style>
